import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import qapi from "views_shared/app/javascript/api";
import Quote from "./Quote";
import QuoteEditor from "./QuoteEditor";
import ConfirmModal from "../confirm_modal";
import ServerErrorAlert from "../profiles/ServerErrorAlert";
import PaginationGroup from "../profiles/PaginationGroup";

const QUOTES_PER_PAGE = 5;

// Self-fetching quotes list for a PromoEntity owner, with an optional owner-editing surface layered on
// top of the original read-only widget. Hosts that supply their own heading (e.g. a profile section
// card) pass showTitle={false}; emptyMessage renders a neutral empty state instead of disappearing
// entirely. onEmptyChange lets a caller that wraps this in its own occupancy guard (e.g. ExpertSections'
// d-none wrapper, which starts hidden and only reveals once told the section isn't empty) learn the
// section's occupancy. Three cases:
//   1. A successful read: onEmptyChange(rows.length === 0) -- the read is the authority on occupancy.
//   2. A failed read (resolved `{ error }` OR rejected promise) with ZERO rows currently on screen:
//      onEmptyChange(false) -- NOT confirmed empty, so the wrapper must reveal or its retry banner
//      would render inert behind a hidden ancestor. A truly-empty profile may briefly show a section
//      with just a retry banner on a transient failure; that's correct -- we genuinely don't know yet.
//   3. A failed read with rows already on screen: onEmptyChange is NOT called -- occupancy is already
//      `false` from the earlier success, and calling it again would be redundant, not incorrect.
// The retry/error state itself (see loadError below) is what an editable OR non-editable viewer sees
// for the failure.
//
// `editable` + `ownerResource` turn on the add/edit/delete affordances: `ownerResource` is the nested
// route segment for the owner ("experts", "companies", "products" -- see Id::Engine routes'
// quotable_and_media_itemable concern). `ownerType` stays the PostgREST/read-side concept (the
// concrete STI owner_type string); the two are never conflated -- a read only ever needs the former,
// a mutation only ever needs the latter.
function Quotes({
  ownerId,
  ownerType,
  ownerResource = undefined,
  editable = false,
  showTitle = true,
  emptyMessage = null,
  onEmptyChange = () => {},
  openAddSignal = 0,
}) {
  const [quotes, setQuotes] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  // Reorder (up/down) and the featured-star toggle share one in-flight flag: both mutate list_position
  // and/or featured server-side, and the row array isn't updated optimistically, so a second click
  // against stale positions/flags before the refetch lands could target the wrong row. Blocking every
  // row's arrows/star for the duration of any one of these mutations -- not just the acting row's --
  // avoids that race; the edit/delete controls are unaffected (their own `disabled` already covers the
  // editingId case above).
  const [rowActionPending, setRowActionPending] = useState(false);
  const [rowActionError, setRowActionError] = useState(null);
  const [page, setPage] = useState(1);

  // `load` must read the CURRENT row count on a failure (case 2 vs. 3 above), but must not be recreated
  // every time `quotes` changes -- doing so would re-fire the effect below on every successful load,
  // an infinite fetch loop. A ref mirrors `quotes` on every render without going in `load`'s own
  // dependency array.
  const quotesRef = useRef(quotes);
  quotesRef.current = quotes;

  // The one awaitable loader for both the initial read AND post-mutation reconciliation. A failed load
  // (a resolved `{ error }` OR a rejected promise -- a network failure rejects rather than resolving an
  // error shape) never overwrites `quotes`: the last successfully loaded rows are retained. See the
  // three-case onEmptyChange contract in the file header comment. The failure is surfaced separately
  // via loadError, which the render below uses to show a retry affordance instead of silently rendering
  // nothing.
  const load = useCallback(async () => {
    let response;
    try {
      response = await qapi.quotes.getAllByOwner(ownerType, ownerId);
    } catch (e) {
      response = { error: e };
    }

    if (Array.isArray(response?.data)) {
      setQuotes(response.data);
      setLoadError(null);
      onEmptyChange(response.data.length === 0);
      return response.data;
    }
    setLoadError(response?.error ?? new Error("Failed to load quotes"));
    if (quotesRef.current.length === 0) {
      onEmptyChange(false);
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, ownerType]);

  useEffect(() => {
    load();
  }, [load]);

  // Lets an outside affordance open the inline add editor by bumping openAddSignal; skip the mount
  // pass so it never opens on load.
  const initialOpenAddSignal = useRef(openAddSignal);
  useEffect(() => {
    if (openAddSignal === initialOpenAddSignal.current) return;
    if (editable) setAdding(true);
  }, [openAddSignal, editable]);

  const showPageOf = (rows, quoteId) => {
    const index = rows ? rows.findIndex((row) => row.id === quoteId) : -1;
    if (index >= 0) {
      setPage(Math.floor(index / QUOTES_PER_PAGE) + 1);
    }
  };

  const createQuote = async (attributes) => {
    const existingIds = new Set(quotes.map((quote) => quote.id));
    await qapi.quotes.createForOwner(ownerResource, ownerId, attributes);
    const rows = await load();
    const created = rows?.find((row) => !existingIds.has(row.id));
    if (created) {
      showPageOf(rows, created.id);
    }
  };

  const updateQuote = async (id, attributes) => {
    await qapi.quotes.updateById(ownerResource, ownerId, id, attributes);
    await load();
  };

  const confirmDelete = async () => {
    if (deleting || pendingDeleteId == null) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await qapi.quotes.deleteById(ownerResource, ownerId, pendingDeleteId);
      setPendingDeleteId(null);
      await load();
    } catch (e) {
      setDeleteError(e);
    } finally {
      setDeleting(false);
    }
  };

  // Moves `quote` to its immediate neighbor's list_position -- a server-side swap (acts_as_list
  // shifts the neighbor the other way), not a delta the client computes on its own. On failure the
  // rows are left exactly as loaded (no optimistic reorder to roll back) and the error surfaces via
  // rowActionError; on success the list reloads from the server so both rows land at their true,
  // server-computed positions.
  const moveQuote = async (quote, direction) => {
    if (rowActionPending) return;
    const currentIndex = quotes.findIndex((q) => q.id === quote.id);
    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex === -1 || targetIndex < 0 || targetIndex >= quotes.length) return;

    setRowActionPending(true);
    setRowActionError(null);
    try {
      await qapi.quotes.updateListPositionById(ownerResource, ownerId, quote.id, quotes[targetIndex].list_position);
      showPageOf(await load(), quote.id);
    } catch (e) {
      setRowActionError(e);
    } finally {
      setRowActionPending(false);
    }
  };

  const toggleFeatured = async (quote) => {
    if (rowActionPending) return;
    setRowActionPending(true);
    setRowActionError(null);
    try {
      await qapi.quotes.setFeaturedById(ownerResource, ownerId, quote.id, !quote.featured);
      showPageOf(await load(), quote.id);
    } catch (e) {
      setRowActionError(e);
    } finally {
      setRowActionPending(false);
    }
  };

  // A non-editable viewer with nothing to show (and no load failure to report) renders the neutral
  // empty state (or nothing) exactly as before -- the editing surface below only ever applies to an
  // editable viewer. A load failure always falls through to the full render below instead: an initial
  // failure has zero local rows, but "zero rows because nothing has loaded yet" must not read the same
  // as "confirmed empty" -- the retry state has to be reachable for every viewer, not just an editor.
  if (!editable && quotes.length === 0 && !loadError) {
    return emptyMessage ? <div className="text-muted">{emptyMessage}</div> : null;
  }

  const pageCount = Math.max(1, Math.ceil(quotes.length / QUOTES_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * QUOTES_PER_PAGE;

  return (
    <>
      {showTitle && (
        <>
          <h3>Quotes</h3>
          <div className="mb-2" />
        </>
      )}

      {loadError && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center">
          <span>
            {quotes.length > 0 ? "Couldn't refresh quotes — showing the last saved list." : "Couldn't load quotes."}
          </span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={load}>
            Retry
          </button>
        </div>
      )}

      {/* Suppressed while loadError is set: a failed read is not a confirmed empty list, so telling the
          viewer "no quotes added yet" alongside the retry banner above would be a contradiction. */}
      {quotes.length === 0 && !adding && !loadError && emptyMessage && (
        <div className="text-muted mb-2">{emptyMessage}</div>
      )}

      {editable && <ServerErrorAlert generalError={rowActionError} />}

      {quotes.slice(pageStart, pageStart + QUOTES_PER_PAGE).map((quote, pageIndex) => {
        const index = pageStart + pageIndex;
        return editable && editingId === quote.id ? (
          <QuoteEditor
            key={quote.id}
            initialQuoteText={quote.quote_text}
            onSave={(attributes) => updateQuote(quote.id, attributes)}
            onClose={() => setEditingId(null)}
          />
        ) : (
          <Quote
            key={quote.id}
            quote={quote}
            editable={editable}
            onEdit={() => setEditingId(quote.id)}
            onDelete={() => {
              setDeleteError(null);
              setPendingDeleteId(quote.id);
            }}
            onMoveUp={() => moveQuote(quote, "up")}
            onMoveDown={() => moveQuote(quote, "down")}
            onToggleFeatured={() => toggleFeatured(quote)}
            // The list reads featured-first, so the array is two contiguous blocks (featured, then
            // unfeatured). A swap across the block boundary would still change list_position server-side
            // but produce no visible reorder (the row stays in its own block after the reload) -- so the
            // arrows are guarded to only move within a row's own block, matching what the button can
            // actually show the viewer. `featured` is nullable (null and false both mean "not featured"),
            // so both sides are normalized with Boolean(...) before comparing -- a raw `===` would treat
            // null and false as different blocks and misdraw the boundary.
            canMoveUp={index > 0 && Boolean(quotes[index - 1].featured) === Boolean(quote.featured)}
            canMoveDown={index < quotes.length - 1 && Boolean(quotes[index + 1].featured) === Boolean(quote.featured)}
            disabled={(editingId != null && editingId !== quote.id) || rowActionPending}
            // A reload after a mutation can fail and leave `quotes` stale (see loadError above); a
            // subsequent arrow/star click would then compute its swap target from that stale order.
            // Blocking reorder/feature while loadError is set forces the viewer through the retry
            // banner first; edit/delete don't touch row order, so they're left as they were.
            reorderDisabled={Boolean(loadError)}
          />
        );
      })}

      <PaginationGroup
        className={editable ? "my-4" : "mt-4"}
        page={currentPage}
        pageCount={pageCount}
        onPageChange={setPage}
        ariaLabel="Quotes pages"
        disabled={editingId != null}
      />

      {editable &&
        (adding ? (
          <QuoteEditor initialQuoteText="" onSave={createQuote} onClose={() => setAdding(false)} autoFocus />
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setAdding(true)}
            disabled={editingId != null}
          >
            Add a quote
          </button>
        ))}

      {pendingDeleteId != null && (
        <ConfirmModal
          confirmYesText="Remove"
          confirmNoText="Cancel"
          confirmContent={
            <>
              <div>Remove this quote?</div>
              <ServerErrorAlert generalError={deleteError} />
            </>
          }
          confirmYes={confirmDelete}
          confirmNo={deleting ? undefined : () => setPendingDeleteId(null)}
          isOpening
          className="modal-sm p-3"
          confirmNoButtonClassName="shadow-none"
          isSubmitting={deleting}
        />
      )}
    </>
  );
}

Quotes.propTypes = {
  ownerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  ownerType: PropTypes.string.isRequired,
  ownerResource: PropTypes.string,
  editable: PropTypes.bool,
  showTitle: PropTypes.bool,
  emptyMessage: PropTypes.string,
  onEmptyChange: PropTypes.func,
  openAddSignal: PropTypes.number,
};

export default Quotes;
